import { useState, useEffect, useRef } from "react";

// ── Design Tokens ──
const gold = "#f5a623", gold2 = "#ffc247";
const blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e";
const red = "#ef4444";
const purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

// ── Scroll Reveal ──
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

// ── Shared atoms ──
const s = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return (
    <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>
      {text}
    </span>
  );
}

// ── Standard vs Behavioural Comparison ──
function CompareTable() {
  const rows = [
    ["Investor Behaviour", "Rational, logical, risk-averse", "Often irrational, emotional, subject to biases"],
    ["Market Efficiency", "Markets are efficient; prices reflect all information", "Markets have anomalies (e.g., January Effect)"],
    ["Basis", "Idealized behaviour — how investors should act", "Observed behaviour — how investors actually act"],
    ["Information Processing", "Perfect and instantaneous", "Selective, distorted by memory and emotion"],
  ];
  return (
    <div style={{ margin: "28px 0", overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["Feature", "Standard Finance", "Behavioural Finance"].map((h, i) => (
              <th key={i} style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: i === 0 ? muted : i === 1 ? blue2 : gold, background: navy4, borderBottom: `2px solid ${i === 1 ? blue : gold}`, borderRight: `1px solid ${border}`, whiteSpace: "nowrap" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? navy3 : navy4 }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: "12px 16px", fontSize: "0.85rem", color: ci === 0 ? white : textCol, borderBottom: `1px solid ${border}`, borderRight: `1px solid ${border}`, lineHeight: 1.6, fontWeight: ci === 0 ? 700 : 400 }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Bias Cards ──
const cognitiveBiases = [
  {
    id: "anchoring", icon: "⚓", name: "Anchoring Bias", type: "cognitive",
    def: "Sticking to a specific price or level (the \"anchor\") and making decisions based on that reference point, ignoring new information.",
    example: "A client refuses to sell a stock until it gets back to the price they paid ($50), even though the company's fundamentals have significantly deteriorated.",
    fix: "Educate the client with updated fundamental analysis. Show that the original purchase price is irrelevant to future performance.",
    pills: ["Logic Error", "Correctable with Education"],
  },
  {
    id: "representativeness", icon: "🪞", name: "Representativeness Bias", type: "cognitive",
    def: "Stereotyping or assuming that recent patterns will continue. Judging probability based on superficial resemblance to a familiar category.",
    example: "Buying a fund solely because it had high returns last year, assuming the manager is a genius, without examining long-term data or risk-adjusted returns.",
    fix: "Present long-term data and base rate statistics to show that recent performance is a weak predictor of future results.",
    pills: ["Logic Error", "Correctable with Data"],
  },
  {
    id: "availability", icon: "📰", name: "Availability Bias", type: "cognitive",
    def: "Estimating the probability of an outcome based on how easily examples come to mind — often driven by recent, vivid, or emotionally charged news.",
    example: "An investor avoids all equities after a market crash because the crash is \"fresh\" in their mind, ignoring the long-term history of market recovery.",
    fix: "Provide historical data on market recovery cycles. Context and base rates help reduce the outsized weight of recent events.",
    pills: ["Memory Error", "Correctable with Context"],
  },
  {
    id: "hindsight", icon: "🔮", name: "Hindsight Bias", type: "cognitive",
    def: "The \"I knew it all along\" effect. Seeing past events as having been predictable or obvious after they have already occurred.",
    example: "A client criticizes you for not selling a stock before a drop, claiming it was \"obvious\" it would fall — even though there was no credible evidence at the time.",
    fix: "Walk through the information that was actually available at the time of the decision. Document rationale for recommendations.",
    pills: ["Memory Distortion", "Correctable with Records"],
  },
];

const emotionalBiases = [
  {
    id: "loss_aversion", icon: "😰", name: "Loss Aversion", type: "emotional",
    def: "The pain of a loss is felt much more intensely than the pleasure of an equivalent gain. The psychological impact of losing $1,000 is roughly 2–2.5× stronger than gaining $1,000.",
    example: "A client holds onto a losing stock to avoid the pain of realizing the loss, hoping it will bounce back — even when fundamentals suggest it won't.",
    fix: "Adapt the portfolio — reduce equity exposure to a level the client can tolerate. A slightly sub-optimal portfolio held long-term beats a perfect portfolio sold in panic.",
    pills: ["Emotional", "Hard to Change", "Adapt Portfolio"],
  },
  {
    id: "overconfidence", icon: "🦁", name: "Overconfidence", type: "emotional",
    def: "Unwarranted faith in one's own reasoning, forecasts, or abilities. Leads to excessive trading, under-diversification, and concentration risk.",
    example: "A client trades stocks frequently, believing they can time the market. Transaction costs and poor timing usually result in returns below the index.",
    fix: "Show documented evidence of their actual trading performance vs. a passive benchmark. Introduce position sizing rules.",
    pills: ["Emotional", "Hard to Change", "Adapt with Guardrails"],
  },
  {
    id: "status_quo", icon: "🪨", name: "Status Quo Bias", type: "emotional",
    def: "The preference for things to stay the same. Inertia prevents action even when change would clearly be beneficial — doing nothing feels safer than deciding.",
    example: "A client leaves money in a low-interest savings account for years simply because moving it requires making a decision.",
    fix: "Frame the decision as \"what would you do with new money today?\" to avoid the current allocation feeling like the default.",
    pills: ["Emotional", "Hard to Change", "Reframe the Choice"],
  },
  {
    id: "endowment", icon: "🎁", name: "Endowment Bias", type: "emotional",
    def: "Valuing an asset more highly simply because you own it. Often driven by emotional attachment, loyalty, or sentimental value rather than market fundamentals.",
    example: "A client inherits shares of a bank and refuses to diversify, feeling a sense of loyalty or family attachment that the market does not share.",
    fix: "Ask: \"If you didn't own this today, would you buy it at this price?\" This reframing helps separate emotional value from market value.",
    pills: ["Emotional", "Hard to Change", "Adapt or Reframe"],
  },
  {
    id: "regret", icon: "😔", name: "Regret Aversion", type: "emotional",
    def: "Avoiding actions to prevent the possibility of future regret. The fear of making a wrong decision causes paralysis — inaction feels \"safer\" even when it isn't.",
    example: "An investor stays entirely out of the market because they are afraid they might buy at a peak and regret it — even though staying out has its own opportunity cost.",
    fix: "Help the client understand that inaction also has a cost. Use dollar-cost averaging to reduce the fear of mistiming the market.",
    pills: ["Emotional", "Hard to Change", "Systematic Approach"],
  },
];

function BiasCard({ bias, accent }) {
  const [open, setOpen] = useState(false);
  return (
    <div onClick={() => setOpen(!open)}
      style={{ background: open ? navy4 : navy3, border: `1px solid ${open ? `${accent}55` : border}`, borderRadius: 8, overflow: "hidden", cursor: "pointer", transition: "all 0.2s" }}>
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: "1.3rem", flexShrink: 0 }}>{bias.icon}</span>
          <div>
            <div style={{ fontSize: "0.9rem", fontWeight: 700, color: white }}>{bias.name}</div>
            <div style={{ fontSize: "0.75rem", color: muted, marginTop: 2 }}>{open ? "Click to collapse" : "Click to expand"}</div>
          </div>
        </div>
        <span style={{ color: open ? accent : muted, fontSize: "1rem", transition: "transform 0.25s", display: "inline-block", transform: open ? "rotate(90deg)" : "none", flexShrink: 0 }}>›</span>
      </div>
      {open && (
        <div style={{ padding: "0 20px 20px", borderTop: `1px solid ${border}` }}>
          <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
            {[
              { label: "Definition", text: bias.def, labelColor: accent },
              { label: "Real-World Example", text: bias.example, labelColor: blue2 },
              { label: "How to Handle It", text: bias.fix, labelColor: green },
            ].map((item, i) => (
              <div key={i} style={{ background: navy3, borderRadius: 6, padding: "12px 16px" }}>
                <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: item.labelColor, marginBottom: 6 }}>{item.label}</div>
                <div style={{ fontSize: "0.85rem", color: textCol, lineHeight: 1.7 }}>{item.text}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14 }}>
            {bias.pills.map((p, i) => <Pill key={i} text={p} color={accent} />)}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Loss Aversion Visual ──
function LossAversionViz() {
  const [gain, setGain] = useState(1000);
  const painMultiplier = 2.25;
  const pain = gain * painMultiplier;
  const maxBar = 280;

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧠 Loss Aversion Visualizer</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>
        Adjust the gain/loss amount to see the asymmetric psychological impact. The <strong style={{ color: textCol }}>pain of a loss</strong> is felt ~2–2.5× more intensely than the pleasure of an equivalent gain.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 28 }}>
        <label style={{ fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", flexShrink: 0 }}>Amount ($)</label>
        <input type="range" min={100} max={10000} step={100} value={gain} onChange={e => setGain(parseInt(e.target.value))}
          style={{ flex: 1, accentColor: gold }} />
        <span style={{ fontFamily: "monospace", fontSize: "0.9rem", color: gold, width: 70, textAlign: "right" }}>${gain.toLocaleString()}</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "end" }}>
        {/* Gain bar */}
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: green, marginBottom: 8 }}>Pleasure of Gain</div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{ fontFamily: "monospace", fontSize: "1rem", color: green }}>+${gain.toLocaleString()}</div>
            <div style={{ width: 60, background: `rgba(34,197,94,.15)`, border: `1px solid rgba(34,197,94,.3)`, borderRadius: "4px 4px 0 0", height: Math.round(maxBar * 0.44), transition: "height 0.4s ease", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 8 }}>
              <span style={{ fontSize: "1.2rem" }}>😊</span>
            </div>
          </div>
          <div style={{ fontSize: "0.75rem", color: muted, marginTop: 8 }}>Psychological Impact: 1×</div>
        </div>

        {/* Loss bar */}
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#f87171", marginBottom: 8 }}>Pain of Loss</div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{ fontFamily: "monospace", fontSize: "1rem", color: "#f87171" }}>−${gain.toLocaleString()}</div>
            <div style={{ width: 60, background: `rgba(239,68,68,.15)`, border: `1px solid rgba(239,68,68,.3)`, borderRadius: "4px 4px 0 0", height: Math.round(maxBar), transition: "height 0.4s ease", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 8 }}>
              <span style={{ fontSize: "1.2rem" }}>😰</span>
            </div>
          </div>
          <div style={{ fontSize: "0.75rem", color: muted, marginTop: 8 }}>Psychological Impact: {painMultiplier}×</div>
        </div>
      </div>

      <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: "14px 18px", marginTop: 20, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontSize: "0.85rem", color: textCol }}>
          To offset the pain of losing <span style={{ color: "#f87171", fontWeight: 700 }}>${gain.toLocaleString()}</span>, you need a gain of approximately:
        </div>
        <div style={{ fontFamily: "monospace", fontSize: "1.3rem", color: gold, fontWeight: 700 }}>
          ${pain.toLocaleString("en-CA", { maximumFractionDigits: 0 })}
        </div>
      </div>
    </div>
  );
}

// ── Moderate vs Adapt ──
function ModerateAdapt() {
  const [active, setActive] = useState(null);
  const cards = [
    {
      type: "moderate", icon: "🎓", accent: blue, label: "Cognitive Biases",
      strategy: "Moderate (Correct)",
      why: "Cognitive biases are based on faulty reasoning or information processing. Since they are logic errors, they can often be corrected through education, better data, and structured discussion.",
      how: ["Present historical data that contradicts the flawed belief", "Walk through base rate statistics", "Show long-term evidence that challenges the anchor or assumption", "Use structured decision frameworks to bypass the shortcut"],
      example: "Client has Anchoring Bias on a stock at $50. Show a DCF analysis proving fair value is $30, regardless of purchase price.",
    },
    {
      type: "adapt", icon: "🤝", accent: gold, label: "Emotional Biases",
      strategy: "Adapt (Accommodate)",
      why: "Emotional biases stem from feelings, intuition, and deeply ingrained psychological patterns. They are much harder to change through education alone — attempting to overrule them often backfires.",
      how: ["Adjust the portfolio to a level the client can emotionally tolerate", "Accept a slightly sub-optimal allocation if it keeps the client invested", "Use dollar-cost averaging to reduce fear of mistiming", "Create clear investment policy statements to anchor decisions during volatility"],
      example: "Client has strong Loss Aversion. Reduce equity from 70% to 55% — slightly sub-optimal, but the client won't panic-sell in a downturn.",
    },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {cards.map((card, i) => (
          <div key={i} onClick={() => setActive(active === i ? null : i)}
            style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? `${card.accent}55` : border}`, borderRadius: 8, padding: 24, cursor: "pointer", transition: "all 0.2s", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${card.accent},transparent)` }} />
            <div style={{ fontSize: "1.6rem", marginBottom: 10 }}>{card.icon}</div>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: card.accent, marginBottom: 6 }}>{card.label}</div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", color: white, marginBottom: 10 }}>{card.strategy}</div>
            <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{card.why}</div>
            {active === i && (
              <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
                <div style={{ background: navy3, borderRadius: 6, padding: "12px 16px" }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: card.accent, marginBottom: 8 }}>How to Do It</div>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {card.how.map((h, j) => (
                      <li key={j} style={{ display: "flex", gap: 8, fontSize: "0.83rem", color: textCol, padding: "5px 0", borderBottom: j < card.how.length - 1 ? `1px solid ${border}` : "none" }}>
                        <span style={{ color: card.accent, flexShrink: 0 }}>→</span>{h}
                      </li>
                    ))}
                  </ul>
                </div>
                <div style={{ background: navy3, borderRadius: 6, padding: "12px 16px" }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: green, marginBottom: 6 }}>Example</div>
                  <div style={{ fontSize: "0.83rem", color: textCol, lineHeight: 1.7 }}>{card.example}</div>
                </div>
              </div>
            )}
            <div style={{ marginTop: 12, fontSize: "0.75rem", color: active === i ? card.accent : muted }}>
              {active === i ? "Click to collapse ↑" : "Click to expand ↓"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Best Practical Allocation ──
function BestPractical() {
  const [equityPct, setEquityPct] = useState(60);
  const bondPct = 100 - equityPct;
  const optimal = 60;
  const diff = Math.abs(equityPct - optimal);
  const isPractical = equityPct !== optimal;

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>⚖️ Strategic vs. Best Practical Allocation</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>
        Drag the slider to see the difference between the mathematically optimal allocation and a "best practical" allocation adjusted for behavioural biases.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <label style={{ fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", flexShrink: 0, width: 72 }}>Equity %</label>
        <input type="range" min={20} max={90} step={5} value={equityPct} onChange={e => setEquityPct(parseInt(e.target.value))} style={{ flex: 1, accentColor: gold }} />
        <span style={{ fontFamily: "monospace", fontSize: "0.9rem", color: gold, width: 40, textAlign: "right" }}>{equityPct}%</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        {/* Portfolio bars */}
        {[
          { label: "Strategic (Optimal)", equity: 60, bond: 40, accent: blue2, tag: "Mathematically optimal" },
          { label: "Best Practical (Yours)", equity: equityPct, bond: bondPct, accent: gold, tag: isPractical ? `Adjusted for bias (−${diff}% equity)` : "Matches optimal allocation" },
        ].map((p, i) => (
          <div key={i} style={{ background: navy4, borderRadius: 8, padding: 18 }}>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: p.accent, marginBottom: 10 }}>{p.label}</div>
            <div style={{ height: 12, borderRadius: 6, overflow: "hidden", background: navy3, marginBottom: 8 }}>
              <div style={{ height: "100%", width: `${p.equity}%`, background: `linear-gradient(90deg,${p.accent},${p.accent}aa)`, transition: "width 0.4s ease" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.78rem" }}>
              <span style={{ color: p.accent }}>Equity: {p.equity}%</span>
              <span style={{ color: muted }}>Bonds: {p.bond}%</span>
            </div>
            <div style={{ fontSize: "0.72rem", color: muted, marginTop: 8, fontStyle: "italic" }}>{p.tag}</div>
          </div>
        ))}
      </div>

      <div style={{ background: navy4, border: `1px solid ${isPractical ? "rgba(245,166,35,.2)" : "rgba(34,197,94,.2)"}`, borderRadius: 8, padding: "14px 18px" }}>
        <div style={{ fontSize: "0.83rem", color: textCol, lineHeight: 1.7 }}>
          {isPractical
            ? <><strong style={{ color: gold }}>Best Practical Principle:</strong> A portfolio with {equityPct}% equity is slightly sub-optimal — but if it means the client <em>stays invested</em> during a downturn instead of panic-selling, it will outperform the "perfect" portfolio in practice.</>
            : <><strong style={{ color: green }}>At Optimal:</strong> The strategic allocation matches the best practical allocation — no bias adjustment needed for this client.</>
          }
        </div>
      </div>
    </div>
  );
}

// ── Quiz ──
const questions = [
  { q: "Which of the following best describes 'Loss Aversion'?", opts: ["The tendency to trade frequently due to overconfidence", "The belief that past performance predicts future results", "Feeling the pain of a loss more intensely than the pleasure of an equivalent gain", "Valuing an asset more simply because you own it"], correct: 2, fb: { y: "Correct! Loss aversion means the psychological pain of a $1,000 loss is roughly 2–2.5× more intense than the pleasure of a $1,000 gain.", n: "Not quite. Loss aversion specifically describes the asymmetric psychological impact of losses vs. gains — losses hurt more than equivalent gains feel good." } },
  { q: "A client refuses to sell a mutual fund that has performed poorly for 5 years because she inherited it from her father and feels it 'belongs' in the family portfolio. Which bias is this?", opts: ["Endowment Bias", "Availability Bias", "Hindsight Bias", "Anchoring Bias"], correct: 0, fb: { y: "Correct! Endowment bias is valuing an asset more highly due to ownership or emotional/sentimental attachment, regardless of market fundamentals.", n: "Not quite. Endowment bias is valuing an asset higher simply because you own it — driven by sentiment or loyalty rather than market value." } },
  { q: "Cognitive biases are generally caused by:", opts: ["Emotional responses to stress", "Faulty reasoning or information processing errors", "A lack of money or financial resources", "Market manipulation by institutional investors"], correct: 1, fb: { y: "Correct! Cognitive biases stem from faulty reasoning, statistical errors, or information processing failures — they are logic errors, not emotional responses.", n: "Not quite. Cognitive biases are errors in logic and information processing. Emotional biases stem from feelings and impulses." } },
  { q: "If a client exhibits a strong emotional bias, the recommended approach for the advisor is generally to:", opts: ["Moderate the bias by educating the client", "Ignore the bias entirely", "Adapt to the bias by adjusting the portfolio allocation", "Refuse to work with the client"], correct: 2, fb: { y: "Correct! Emotional biases are deeply ingrained and hard to change through education. The best practical approach is to adapt the portfolio to accommodate the bias so the client stays invested.", n: "Not quite. Emotional biases can't easily be educated away. The advisor should adapt the portfolio allocation — the 'Best Practical Allocation' — to accommodate the bias." } },
  { q: "An investor assumes that because technology stocks rose 20% last year, they will definitely rise 20% this year. This is an example of:", opts: ["Representativeness Bias", "Regret Aversion", "Status Quo Bias", "Anchoring Bias"], correct: 0, fb: { y: "Correct! Representativeness bias involves stereotyping — assuming the recent past will continue based on superficial pattern matching, without examining base rates or fundamentals.", n: "Not quite. Representativeness bias is stereotyping the future based on the recent past — assuming past performance patterns will repeat." } },
  { q: "'Best Practical Allocation' refers to:", opts: ["The portfolio with the highest possible mathematical return", "An allocation that ignores the client's risk profile", "An allocation that adjusts the optimal mix to accommodate behavioural biases so the client stays invested", "Putting all assets into GICs"], correct: 2, fb: { y: "Correct! Best Practical Allocation is the portfolio the client can actually live with — slightly sub-optimal mathematically, but one that prevents panic-selling during volatility.", n: "Not quite. Best Practical Allocation means adjusting the mathematically optimal portfolio to accommodate the client's biases, ensuring they stay invested long-term." } },
  { q: "Which of the following is a characteristic of Standard (Traditional) Finance?", opts: ["Investors are irrational", "Markets are inefficient and full of anomalies", "Investors are risk-averse and rational", "Prices are driven by investor sentiment"], correct: 2, fb: { y: "Correct! Standard Finance assumes investors are rational, risk-averse, and process information perfectly — the idealized model of behaviour.", n: "Not quite. Standard Finance assumes rational, risk-averse investors and perfectly efficient markets. Behavioural Finance challenges these assumptions." } },
  { q: "A client tells you: 'I knew the market was going to crash! It was so obvious!' — despite having been fully invested right before the crash. This is:", opts: ["Overconfidence Bias", "Hindsight Bias", "Availability Bias", "Anchoring Bias"], correct: 1, fb: { y: "Correct! Hindsight bias is the 'I knew it all along' effect — viewing past events as having been predictable after the fact, even when there was no credible prior evidence.", n: "Not quite. This is hindsight bias — the tendency to see past events as obvious in retrospect, even though they weren't predictable at the time." } },
  { q: "Overconfidence bias often results in:", opts: ["Excessive trading and under-diversification", "Holding too much cash and avoiding risk", "Buying low-cost index funds passively", "Fear of entering the market"], correct: 0, fb: { y: "Correct! Overconfident investors believe they can beat the market, leading to excessive trading (high transaction costs) and concentrated positions (under-diversification).", n: "Not quite. Overconfidence leads to excessive trading and concentration — the investor believes they can time the market and pick winners better than others." } },
  { q: "The 'January Effect' is an example of:", opts: ["A rational market outcome consistent with the Efficient Market Hypothesis", "A market anomaly that challenges the Efficient Market Hypothesis", "A regulatory rule governing fiscal year reporting", "A type of mutual fund investment strategy"], correct: 1, fb: { y: "Correct! The January Effect (stocks tend to rise in January) is a market anomaly — a predictable pattern that shouldn't exist in a perfectly efficient market, suggesting behavioural forces are at work.", n: "Not quite. The January Effect is a market anomaly — a recurring pattern that shouldn't exist if markets were perfectly rational and efficient, as behavioural finance argues." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;

  function answer(idx) {
    if (answered[current] !== undefined) return;
    setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } });
  }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }

  const q = questions[current];
  const ans = answered[current];
  const pct = Math.round(score / total * 100);

  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 5 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>

      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) {
                if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; }
                else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; }
              }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>
                    {["A","B","C","D"][i]}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. You've mastered behavioural finance." : score >= 8 ? "Excellent. Review the biases you missed." : score >= 6 ? "Solid start. Revisit the cognitive vs. emotional distinction." : "Go back through the bias cards — focus on definitions and examples."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "Behavioural Finance", def: "A field combining psychology and economics to explain why investors act irrationally, influenced by emotions and cognitive shortcuts." },
  { term: "Standard Finance", def: "Traditional financial theory assuming rational, risk-averse investors who process information perfectly and markets that are always efficient." },
  { term: "Cognitive Bias", def: "A systematic error in thinking caused by faulty reasoning or information processing. Can often be corrected through education and better data." },
  { term: "Emotional Bias", def: "A bias driven by feelings, intuition, or impulses. Hard to correct — advisors generally adapt the portfolio rather than trying to eliminate the bias." },
  { term: "Anchoring Bias", def: "Over-relying on an initial piece of information (the anchor) when making decisions, even when that reference point is irrelevant." },
  { term: "Representativeness Bias", def: "Judging the probability of an event by how closely it resembles a familiar pattern, often leading to overweighting recent trends." },
  { term: "Availability Bias", def: "Overestimating the probability of events that come easily to mind — typically recent, vivid, or emotionally charged events." },
  { term: "Hindsight Bias", def: 'The "I knew it all along" effect — believing past events were more predictable than they actually were at the time.' },
  { term: "Loss Aversion", def: "The tendency for the psychological pain of a loss to be felt 2–2.5× more intensely than the pleasure of an equivalent gain." },
  { term: "Overconfidence", def: "Unwarranted faith in one's own judgment or abilities, leading to excessive trading, under-diversification, and concentrated risk." },
  { term: "Status Quo Bias", def: "The preference for the current state of affairs; inertia prevents making a change even when the change would be beneficial." },
  { term: "Endowment Bias", def: "Valuing an asset more highly simply because you own it — driven by emotional attachment rather than objective market value." },
  { term: "Regret Aversion", def: "Avoiding action to prevent the possibility of future regret. Fear of making a wrong decision causes paralysis even when inaction has its own costs." },
  { term: "Best Practical Allocation", def: "The portfolio the client can actually live with — slightly sub-optimal mathematically, but adjusted for biases to prevent panic-selling." },
  { term: "Market Anomaly", def: "A pattern or tendency in markets that contradicts the Efficient Market Hypothesis (e.g., January Effect). Evidence that behaviour affects prices." },
  { term: "Moderate (Cognitive)", def: "The advisor strategy for cognitive biases: educate, provide data, and correct the flawed reasoning to move toward the optimal allocation." },
  { term: "Adapt (Emotional)", def: "The advisor strategy for emotional biases: adjust the portfolio to accommodate the bias rather than trying to eliminate it." },
];

// ── Main Export ──
export default function Chapter5() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "Introduction to Behavioural Finance"],
    ["02", "Cognitive Biases — The 4 Mental Blind Spots"],
    ["03", "Emotional Biases — The 5 Feeling-Based Errors"],
    ["04", "Moderate vs. Adapt Strategy"],
    ["05", "Best Practical Allocation"],
    ["🧠", "Loss Aversion Visualizer"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        {/* HERO */}
        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 5
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              Behavioural <span style={{ color: gold }}>Finance</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              Why investors act irrationally — the cognitive and emotional biases that distort decision-making, how to diagnose them, and how to build portfolios clients can actually live with.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","5","Core Topics"],["⏱","~16 min","read"],["🎯","10","practice questions"],["🧠","9","bias profiles"],["🧮","2","interactive tools"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* TOC */}
        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* ── S1: Intro ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 01</span><span style={s.badge}>Foundations</span><div style={s.line} /></div>
            <h2 style={s.h2}>Introduction to Behavioural Finance</h2>
            <p style={s.prose}>
              <strong style={{ color: white }}>Traditional (Standard) Finance</strong> assumes investors are perfectly rational, always risk-averse, and process all information instantly and correctly.{" "}
              <span style={{ color: gold, fontWeight: 600 }}>Behavioural Finance</span> challenges this — it combines psychology and economics to explain why investors <em>actually</em> behave the way they do, acknowledging that humans are often irrational and heavily influenced by emotion.
            </p>
          </Reveal>
          <Reveal delay={0.1}><CompareTable /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="💼">
              <strong>Why This Matters for Advisors:</strong> Understanding behavioural finance helps you (1) build stronger client relationships by understanding their true motivations, (2) construct portfolios clients can <em>"live with"</em> during volatility, and (3) clearly distinguish between a client's <strong>financial capacity</strong> for risk and their <strong>emotional capacity</strong> for risk — two very different things.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S2: Cognitive Biases ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 02</span><span style={s.badge}>Cognitive Biases</span><div style={s.line} /></div>
            <h2 style={s.h2}>Cognitive Biases — The Mental Blind Spots</h2>
            <p style={s.prose}>
              Cognitive biases are <span style={{ color: blue2, fontWeight: 600 }}>basic statistical, information-processing, or memory errors</span>. They stem from faulty reasoning — not emotions. Because they are logic errors, they can often be <strong style={{ color: white }}>corrected through education and better data</strong>. Click each bias to expand its full profile:
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <span style={{ display: "inline-block", fontSize: "0.68rem", fontWeight: 700, padding: "4px 12px", borderRadius: 100, color: blue2, background: "rgba(59,130,246,.1)", border: "1px solid rgba(59,130,246,.2)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Cognitive</span>
              <span style={{ fontSize: "0.83rem", color: muted }}>Faulty reasoning → Correctable with education</span>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {cognitiveBiases.map(b => <BiasCard key={b.id} bias={b} accent={blue2} />)}
            </div>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S3: Emotional Biases ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 03</span><span style={s.badge}>Emotional Biases</span><div style={s.line} /></div>
            <h2 style={s.h2}>Emotional Biases — The Feeling-Based Errors</h2>
            <p style={s.prose}>
              Emotional biases stem from <span style={{ color: gold, fontWeight: 600 }}>feelings, intuition, and deeply ingrained psychological impulses</span>. They are far harder to correct because they are not based on reasoning — trying to "educate them away" often backfires. The recommended strategy is usually to <strong style={{ color: white }}>adapt the portfolio</strong> to accommodate them. Click each bias to expand:
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <span style={{ display: "inline-block", fontSize: "0.68rem", fontWeight: 700, padding: "4px 12px", borderRadius: 100, color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Emotional</span>
              <span style={{ fontSize: "0.83rem", color: muted }}>Feeling-driven → Adapt the portfolio</span>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {emotionalBiases.map(b => <BiasCard key={b.id} bias={b} accent={gold} />)}
            </div>
          </Reveal>
          <Reveal delay={0.2}><LossAversionViz /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S4: Moderate vs Adapt ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 04</span><span style={s.badge}>Advisor Strategy</span><div style={s.line} /></div>
            <h2 style={s.h2}>Moderate vs. Adapt Strategy</h2>
            <p style={s.prose}>
              Once you identify a bias, you need a clear strategy. The approach depends entirely on whether the bias is <span style={{ color: blue2, fontWeight: 600 }}>cognitive</span> (logic error → educate) or <span style={{ color: gold, fontWeight: 600 }}>emotional</span> (feeling-driven → accommodate). Click each card to see the full playbook:
            </p>
          </Reveal>
          <Reveal delay={0.1}><ModerateAdapt /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="🎯" accent={green}>
              <strong style={{ color: green }}>The Golden Rule of Behavioural Advising:</strong> It is better to have a slightly sub-optimal portfolio that the client <em>holds long-term</em> than a mathematically "perfect" portfolio that the client <em>sells in a panic</em> during a market downturn. Behaviour matters more than basis points.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S5: Best Practical ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 05</span><span style={s.badge}>Portfolio Construction</span><div style={s.line} /></div>
            <h2 style={s.h2}>Best Practical Allocation</h2>
            <p style={s.prose}>
              The <span style={{ color: gold, fontWeight: 600 }}>Strategic Asset Allocation</span> is the mathematically optimal mix derived from a client's risk profile (e.g., 60% Equity / 40% Bond). The <strong style={{ color: white }}>Best Practical Allocation</strong> is what the client can actually live with — adjusted for their behavioural biases. Use the interactive tool below:
            </p>
          </Reveal>
          <Reveal delay={0.1}><BestPractical /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Quiz ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Knowledge Check</span><span style={s.badge}>10 Questions</span><div style={s.line} /></div>
            <h2 style={s.h2}>Test Your Understanding</h2>
            <p style={s.prose}>10 questions covering all biases and concepts from Chapter 5. Detailed feedback on every answer.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Glossary ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Quick Reference</span><span style={s.badge}>Key Terms</span><div style={s.line} /></div>
            <h2 style={s.h2}>Glossary</h2>
            <p style={s.prose}>All essential terms from Chapter 5 — behavioural biases, advisor strategies, and key concepts.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
